/**
 * Escapadas Low Cost — data-driven micro-guides with affiliate links.
 *
 * How to add a new escapada:
 *   1. Duplicate any object below.
 *   2. Update `slug` (used in URL), title, hero_image, summary, and content.
 *   3. Fill affiliate links (flight/hotel/activities/insurance).
 *   4. Save. The card appears automatically in the landing section and
 *      `/escapadas/<slug>` renders the full guide.
 */

export const ESCAPADAS = [
  {
    slug: "cote-dazur",
    title: "Fin de semana en Niza, Mónaco y Villefranche-sur-Mer",
    tagline:
      "Un fin de semana perfecto para descubrir la Costa Azul: recorrer Mónaco, relajarte en las playas de Villefranche-sur-Mer y terminar paseando por Niza. Todo a poco más de una hora de vuelo desde Barcelona.",
    // Landing card fields
    card_title: "Côte d'Azur",
    card_subtitle: "Niza · Mónaco · Villefranche-sur-Mer",
    price_from: "desde 220 € p.p.",
    // NOTE: hero_image + gallery use local photos in /public/escapadas/cote-dazur/
    hero_image: "/escapadas/cote-dazur/hero-monaco-boy.jpg",
    gallery: [
      { src: "/escapadas/cote-dazur/nice-beach-club.jpg",   caption: "Aéroport Nice Beach Club" },
      { src: "/escapadas/cote-dazur/monaco-casino-2.jpg",   caption: "Plaza del Casino de Montecarlo" },
      { src: "/escapadas/cote-dazur/monaco-casino.jpg",     caption: "Paseo hacia el Casino, Mónaco" },
      { src: "/escapadas/cote-dazur/villefranche-lunch.jpg", caption: "Comida frente al mar en Villefranche-sur-Mer" },
      { src: "/escapadas/cote-dazur/promenade-sunset.jpg",  caption: "Atardecer en la Promenade des Anglais" },
      { src: "/escapadas/cote-dazur/ibis-welcome.jpg",      caption: "Bienvenida al Ibis Nice Centre Gare" },
    ],
    country: "Francia",
    published_at: "2026-07-30",
    // Summary block
    summary: {
      destination: "Niza, Mónaco y Villefranche-sur-Mer",
      flight: "Barcelona → Niza · 1 h 25 min",
      duration: "2 días · fin de semana",
      ideal_for: "Familias, parejas y amantes de las escapadas urbanas",
      highlight:
        "Recorrer Mónaco con el autobús turístico y descubrir uno de los pueblos más bonitos de la Costa Azul.",
    },
    intro:
      "Hay destinos que parecen reservados para grandes presupuestos, pero Mónaco demuestra justo lo contrario.\nEn solo un fin de semana puedes conocer tres lugares completamente diferentes: la elegancia de Mónaco, el encanto marinero de Villefranche-sur-Mer y el ambiente mediterráneo de Niza.",
    // Days: array of sections. Each section can contain paragraphs (array of
    // strings) and optional inline CTAs.
    days: [
      {
        title: "Día 1 · Descubriendo Mónaco",
        paragraphs: [
          "Salimos de Barcelona a primera hora y aterrizamos en Niza poco después de las ocho de la mañana. Desde el aeropuerto nos dirigimos al hotel para dejar las mochilas y comenzamos la aventura.",
          "Nuestro primer destino fue Mónaco. Desde la estación de Nice Ville el trayecto en tren apenas dura unos veinte minutos, pero el recorrido ya merece la pena. Durante el viaje disfrutas de unas vistas espectaculares del Mediterráneo que hacen que el trayecto forme parte de la experiencia.",
          "Nada más llegar decidimos recorrer el Principado con el autobús turístico Monaco Le Grand Tour. Fue, sin duda, una de las mejores decisiones del viaje. En poco más de una hora conocimos los principales rincones de la ciudad y después pudimos bajar en nuestras paradas favoritas.",
          "Visitamos el Palacio del Príncipe, disfrutamos del famoso mirador sobre el puerto, paseamos por la Plaza del Casino de Montecarlo y recorrimos algunas de las zonas más emblemáticas del Principado. Si viajas en verano, el autobús turístico también es una forma perfecta de evitar largas caminatas bajo el sol.",
        ],
        inline_cta: {
          label: "Reserva el autobús turístico y otras actividades en Mónaco",
          href: "https://www.getyourguide.com/es-es/?partner_id=TKM0GU7&cmp=share_to_earn&visitor-id=5QHTAP4XY98CNL5SG906UI3LZ31NAMBO",
          provider: "GetYourGuide",
        },
        outro:
          "Después de visitar Mónaco regresamos a Niza para descansar y disfrutar del resto de la tarde.",
      },
      {
        title: "Noche en Niza",
        paragraphs: [
          "Nos alojamos en el Ibis Centre Gare, una ubicación perfecta para movernos en tren.",
          "Al atardecer dimos un paseo por la ciudad y terminamos el día disfrutando del ambiente de la Costa Azul.",
        ],
        inline_cta: {
          label: "Consulta disponibilidad del hotel",
          href: "https://www.jdoqocy.com/click-101731687-15734352",
          provider: "Booking.com",
        },
      },
      {
        title: "Día 2 · Villefranche-sur-Mer y paseo por Niza",
        paragraphs: [
          "Después del desayuno volvimos a coger el tren, esta vez con destino a Villefranche-sur-Mer. En apenas unos minutos llegas a uno de los pueblos más bonitos de toda la Riviera Francesa.",
          "Paseamos por su puerto, recorrimos las calles del casco antiguo y disfrutamos de unas horas en la playa, con un agua cristalina que recuerda a destinos mucho más lejanos.",
          "Al mediodía comimos frente al mar antes de regresar tranquilamente a Niza.",
        ],
      },
      {
        title: "Últimas horas en Niza",
        paragraphs: [
          "Ya por la tarde aprovechamos para recorrer el centro histórico de Niza. Paseamos por la Promenade des Anglais, disfrutamos del ambiente de sus calles y terminamos la escapada con un helado frente al Mediterráneo antes de poner rumbo al aeropuerto.",
          "Una forma perfecta de despedirse de la Costa Azul.",
        ],
      },
      {
        title: "Regreso a Barcelona",
        paragraphs: [
          "Al caer la tarde volvimos al aeropuerto de Niza para regresar a Barcelona. En apenas dos días habíamos descubierto tres lugares completamente diferentes y con mucho encanto.",
        ],
      },
    ],
    tips: [
      "Coger uno de los primeros trenes hacia Mónaco para evitar las horas de más calor.",
      "Recorrer primero la vuelta completa del autobús turístico y después bajar solo en las paradas que más te interesen.",
      "Reservar el hotel cerca de una estación de tren para moverte fácilmente por toda la Costa Azul.",
      "Dedicar una noche más si quieres visitar también Èze, Menton o Cannes.",
    ],
    opinion:
      "Nos sorprendió muchísimo lo fácil que resulta recorrer la Costa Azul utilizando únicamente el tren. En solo dos días combinamos una ciudad elegante como Mónaco, un pueblo costero con muchísimo encanto como Villefranche-sur-Mer y una ciudad tan agradable como Niza. Es una escapada perfecta para cualquier época del año y una de esas rutas que recomendaríamos sin dudar.",
    // Bottom "Planifica tu escapada" affiliate block
    affiliates: [
      {
        label: "Vuelos",
        provider: "Vueling",
        href: "https://www.anrdoezrs.net/click-101731687-13572630",
        icon: "plane",
      },
      {
        label: "Hotel",
        provider: "Booking.com",
        href: "https://www.jdoqocy.com/click-101731687-15734352",
        icon: "bed",
      },
      {
        label: "Actividades y entradas",
        provider: "GetYourGuide",
        href: "https://www.getyourguide.com/es-es/?partner_id=TKM0GU7&cmp=share_to_earn&visitor-id=5QHTAP4XY98CNL5SG906UI3LZ31NAMBO",
        icon: "ticket",
      },
      {
        label: "Seguro de viaje",
        provider: "IATI Seguros",
        href: "https://clk.tradedoubler.com/click?p=403039&a=3480623&g=26173470",
        icon: "shield",
      },
    ],
  },
  {
    slug: "sigean",
    title: "Fin de semana en la Réserve Africaine de Sigean, Gruissan y Narbonne",
    tagline:
      "Leones, casas flotantes, salinas rosadas y pueblos con encanto a menos de tres horas de Barcelona. Una escapada perfecta para hacer en familia y desconectar durante un fin de semana.",
    card_title: "Sigean",
    card_subtitle: "Safari, casas flotantes & Narbonne",
    price_from: "",
    hero_image: "/escapadas/sigean/hero-zebras.jpg",
    gallery: [
      { src: "/escapadas/sigean/safari-zebras.jpg",       caption: "Cebras en la Réserve Africaine de Sigean" },
      { src: "/escapadas/sigean/ilots-sunset.jpg",        caption: "Les Îlots de Gruissan al atardecer" },
      { src: "/escapadas/sigean/salinas-sunset.jpg",      caption: "Les Salines" },
      { src: "/escapadas/sigean/narbonne-bridge.jpg",     caption: "Narbonne" },
      { src: "/escapadas/sigean/pont-des-marchands.jpg",  caption: "Narbonne" },
    ],
    country: "Francia",
    published_at: "2026-08-03",
    summary: {
      destination: "Sigean, Gruissan y Narbonne",
      flight: "Barcelona → Sigean · menos de 3 h en coche",
      duration: "2 días · fin de semana",
      ideal_for: "Familias, parejas y amantes de la naturaleza",
      highlight:
        "Dormir en una casa flotante frente al mar y recorrer un safari en coche entre animales salvajes.",
    },
    intro:
      "Hay escapadas que disfrutas y otras que recuerdas durante años. Esta es de las segundas.\nBuscábamos un plan diferente para hacer en familia, sin conducir demasiadas horas y combinando naturaleza, animales y algún lugar con encanto donde pasar la noche. El resultado fue un fin de semana que superó todas nuestras expectativas.\nSi estás buscando una escapada original desde Barcelona, esta ruta por el sur de Francia es una apuesta segura.",
    days: [
      {
        title: "Día 1 · Safari en la Réserve Africaine de Sigean",
        paragraphs: [
          "Salimos de Barcelona temprano para aprovechar bien el día. En menos de tres horas ya estábamos en la Réserve Africaine de Sigean, uno de los parques de animales más impresionantes que hemos visitado.",
          "Antes de llegar hicimos una parada en el área de servicio de L'Empordà. Es amplia, tranquila y perfecta para estirar las piernas, tomar un café o repostar antes de cruzar la frontera.",
          "Nada más entrar al parque comienza la parte más espectacular: el recorrido en coche. Y aquí llega la magia. Durante varios kilómetros circulas entre animales en semilibertad mientras ellos hacen su vida completamente ajenos a los visitantes.",
          "Ver un león descansando a pocos metros del coche, varios rinocerontes cruzando la carretera o una manada de cebras galopando delante de ti es una sensación difícil de explicar.",
          "Nuestro momento favorito llegó cuando una avestruz decidió acercarse al coche y empezó a picotear la ventanilla como si quisiera saludarnos. Todavía hoy seguimos riéndonos al recordarlo.",
          "Es una experiencia que sorprende tanto a los niños como a los adultos.",
        ],
        inline_cta: {
          label: "Si necesitas coche para la escapada, consulta las opciones de alquiler aquí",
          href: "https://www.jdoqocy.com/click-101731687-15734352",
          provider: "Booking.com",
        },
      },
      {
        title: "Paseo por la reserva",
        paragraphs: [
          "Después del safari continuamos la visita a pie. El recorrido completo dura aproximadamente dos horas y media y permite observar muchas especies con mucha más tranquilidad.",
          "Si viajas durante los meses más calurosos, lleva agua abundante y protección solar porque gran parte del recorrido transcurre al aire libre.",
          "Dentro del parque encontrarás varios restaurantes y zonas de picnic, así que puedes elegir entre comer allí o llevar tu propia comida.",
          "Un consejo importante: respeta siempre las indicaciones del personal. Los animales tienen prioridad y las normas están para garantizar la seguridad de todos.",
        ],
      },
      {
        title: "Atardecer en Gruissan y noche en una casa flotante",
        paragraphs: [
          "Tras terminar la visita pusimos rumbo a Gruissan. En apenas media hora pasas del ambiente salvaje del safari a uno de los pueblos costeros con más encanto del sur de Francia.",
          "Pero lo mejor todavía estaba por llegar.",
          "Esa noche nos alojamos en Les Îlots de Gruissan, unas casas flotantes construidas sobre el agua que convierten una simple noche de hotel en una experiencia.",
          "Despertarte rodeado de agua, desayunar mirando por la ventana y disfrutar del silencio absoluto hacen que merezca completamente la pena.",
          "Es uno de esos alojamientos que recuerdas mucho después del viaje.",
        ],
        inline_cta: {
          label: "Consulta disponibilidad del alojamiento",
          href: "https://www.jdoqocy.com/click-101731687-15734352",
          provider: "Booking.com",
        },
      },
      {
        title: "Las Salinas de Gruissan",
        paragraphs: [
          "Antes de que anocheciera aprovechamos para visitar las famosas salinas de Gruissan.",
          "El contraste entre el blanco de la sal, los tonos rosados del agua y la luz del atardecer crea un paisaje espectacular.",
          "Eso sí, si viajas en verano lleva repelente para los mosquitos porque aparecen en cuanto baja el sol. Aun así, merece muchísimo la pena acercarse.",
        ],
      },
      {
        title: "Día 2 · Descubriendo Narbonne",
        paragraphs: [
          "A la mañana siguiente recibimos el desayuno directamente en nuestra casa flotante. Fue uno de esos pequeños detalles que hacen especial una escapada.",
          "Después pusimos rumbo a Narbonne, una ciudad elegante, tranquila y perfecta para recorrer caminando.",
          "Paseamos por su casco histórico, cruzamos el famoso Pont des Marchands y decidimos subir al trenecito turístico para conocer cómodamente los principales rincones de la ciudad.",
          "Antes de volver a casa nos sentamos en una terraza del centro para comer algo ligero y, cómo no, terminamos la visita con un buen helado. Un final perfecto para el viaje.",
        ],
      },
      {
        title: "Regreso a Barcelona",
        paragraphs: [
          "Sobre las tres y media de la tarde iniciamos la vuelta.",
          "Si dispones de un día más, puedes aprovechar el regreso para visitar Carcassonne o Perpiñán, dos ciudades que también merecen muchísimo la pena.",
        ],
      },
    ],
    tips: [
      "Llegar al safari a primera hora para recorrerlo con más tranquilidad.",
      "Llevar repelente para las salinas durante el verano.",
      "Reservar la casa flotante con bastante antelación, especialmente en temporada alta.",
      "Dedicar una noche extra si quieres visitar también Carcassonne.",
    ],
    opinion:
      "Esta escapada nos sorprendió muchísimo. En solo dos días combina aventura, naturaleza, animales, pueblos con encanto, buena gastronomía y un alojamiento muy diferente. Es un viaje muy fácil de organizar, sin largas horas de carretera y perfecto tanto para familias como para parejas que quieran hacer algo distinto cerca de casa. Sin duda, es una de esas escapadas que volveríamos a repetir sin pensarlo.",
    affiliates: [
      {
        label: "Alquiler de coche",
        provider: "Booking.com",
        href: "https://www.jdoqocy.com/click-101731687-15734352",
        icon: "plane",
      },
      {
        label: "Alojamiento",
        provider: "Booking.com",
        href: "https://www.jdoqocy.com/click-101731687-15734352",
        icon: "bed",
      },
      {
        label: "Actividades y entradas",
        provider: "GetYourGuide",
        href: "https://www.getyourguide.com/es-es/?partner_id=TKM0GU7&cmp=share_to_earn&visitor-id=5QHTAP4XY98CNL5SG906UI3LZ31NAMBO",
        icon: "ticket",
      },
      {
        label: "Seguro de viaje",
        provider: "IATI Seguros",
        href: "https://clk.tradedoubler.com/click?p=403039&a=3480623&g=26173470",
        icon: "shield",
      },
    ],
  },
];

// Placeholder future escapadas — set `coming_soon: true` on the landing card.
export const ESCAPADAS_COMING_SOON = [];
